// Copyright (c) 2019-present Ladifire, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React from 'react';
import memoize from 'memoize-one';
import classNames from 'classnames';
import {omit} from 'lodash';

import GlobalTheme from '../../theme/components';
import Spinner, {SpinnerSizes} from '../spinner';

import {Theme} from './theme';
import Content from './content';
import InnerWrapper from './inner_wrapper';
import {ButtonProps, ThemeMode, ThemeProps, ThemeTokens} from './types';
import {mapAttributesToState, filterProps, composeRefs} from './utils';

import styles from '../../styles/components/button/style.scss';

export type ButtonState = {
    isHover: boolean;
    isActive: boolean;
    isFocus: boolean;
};

export class Button extends React.PureComponent<ButtonProps, ButtonState> {
    static defaultProps = {
        appearance: 'default',
        autoFocus: false,
        isDisabled: false,
        isTransparent: false,
        isLoading: false,
        isSelected: false,
        shouldFitContainer: false,
        spacing: 'default',
        type: 'button',
        onlyIcon: false,
        isRounded: false,
        isOutline: false,
    };

    // ref can be a range of things because we render button, a, span or other React components
    button: React.Ref<HTMLButtonElement> = React.createRef<HTMLButtonElement>();

    // Makes sure we don't call ref every render.
    getComposedRefs = memoize(composeRefs);

    state = {
        isActive: false,
        isFocus: false,
        isHover: false,
    };

    componentDidMount() {
        if (this.props.autoFocus && this.button instanceof HTMLButtonElement) {
            this.button.focus();
        }
    }

    isInteractive = () => !this.props.isDisabled && !this.props.isLoading;

    onMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        this.setState({isHover: true});
        if (this.props.onMouseEnter) {
            this.props.onMouseEnter(e);
        }
    };

    onMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
        this.setState({isHover: false, isActive: false});
        if (this.props.onMouseLeave) {
            this.props.onMouseLeave(e);
        }
    };

    onMouseDown = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        this.setState({isActive: true});
        if (this.props.onMouseDown) {
            this.props.onMouseDown(e);
        }
    };

    onMouseUp = (e: React.MouseEvent<HTMLElement>) => {
        this.setState({isActive: false});
        if (this.props.onMouseUp) {
            this.props.onMouseUp(e);
        }
    };

    onFocus: React.FocusEventHandler<HTMLButtonElement> = (event) => {
        this.setState({isFocus: true});
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    onBlur: React.FocusEventHandler<HTMLButtonElement> = (event) => {
        this.setState({isFocus: false});
        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    };

    getElement = () => {
        const {href, isDisabled} = this.props;
        if (href) {
            return isDisabled ? 'span' : 'a';
        }
        return 'button';
    };

    // Swallow click events when the button is disabled
    // to prevent inner child clicks bubbling up.
    onInnerClick: React.MouseEventHandler<HTMLElement> = (e) => {
        if (!this.isInteractive()) {
            e.stopPropagation();
        }
        return true;
    };

    public render() {
        const {
            appearance = 'default',
            children,
            className,
            component: CustomComponent,
            consumerRef,
            iconAfter,
            iconBefore,
            isDisabled = false,
            isLoading = false,
            isSelected = false,
            shouldFitContainer = false,
            spacing = 'default',
            onlyIcon,
            theme = (
                current: (props: ThemeProps) => ThemeTokens,
                props: ThemeProps,
            ) => current(props),
            testId,
            ...rest
        } = this.props;

        const attributes = {...this.state, isSelected, isDisabled};

        const StyledButton: React.ReactType = CustomComponent || this.getElement();

        const iconIsOnlyChild = Boolean((iconBefore && !iconAfter && !children) ||
            (iconAfter && !iconBefore && !children));

        // const specifiers = (styles: {}) => {
        //     if (StyledButton === 'a') {
        //         return {
        //             'a&': styles,
        //         };
        //     } else if (StyledButton === CustomComponent) {
        //         return {
        //             '&, a&, &:hover, &:active, &:focus': styles,
        //         };
        //     }
        //     return styles;
        // };

        const spinnerSize: SpinnerSizes = spacing === 'default' ? 'medium' : 'small';

        const props = {...filterProps(rest, StyledButton)};
        const buttonProps = omit(props, ['isTransparent', 'isRounded', 'isOutline']);

        return (
            <Theme.Provider value={theme}>
                <GlobalTheme.Consumer>
                    {({mode}: { mode: ThemeMode }) => (
                        <Theme.Consumer
                            mode={mode}
                            state={mapAttributesToState(attributes)}
                            iconIsOnlyChild={iconIsOnlyChild}
                            {...this.props}
                        >
                            {({classes}) => (
                                <StyledButton
                                    {...buttonProps}
                                    data-testid={testId}
                                    ref={this.getComposedRefs(this.button, consumerRef)}
                                    onMouseEnter={this.onMouseEnter}
                                    onMouseLeave={this.onMouseLeave}
                                    onMouseDown={this.onMouseDown}
                                    onMouseUp={this.onMouseUp}
                                    onFocus={this.onFocus}
                                    onBlur={this.onBlur}
                                    disabled={isDisabled}
                                    className={classNames(classes, className)}
                                >
                                    <InnerWrapper
                                        onClick={this.onInnerClick}
                                        fit={Boolean(shouldFitContainer)}
                                    >
                                        {isLoading && (
                                            <span className={styles.spinner__wrapper}>
                                                <Spinner
                                                    size={spinnerSize}
                                                />
                                            </span>
                                        )}
                                        {iconBefore && (
                                            <span
                                                className={classNames(styles.icon__wrapper, styles[`spacing__${spacing}`], {
                                                    [styles.is_loading]: isLoading,
                                                    [styles.is__only_child]: iconIsOnlyChild,
                                                })}
                                            >
                                                {iconBefore}
                                            </span>
                                        )}
                                        {children && !onlyIcon && (
                                            <Content
                                                isLoading={isLoading}
                                                followsIcon={Boolean(iconBefore)}
                                                spacing={spacing}
                                            >
                                                {children}
                                            </Content>
                                        )}
                                        {iconAfter && (
                                            <span
                                                className={classNames(styles.icon__wrapper, styles[`spacing__${spacing}`], {
                                                    [styles.is_loading]: isLoading,
                                                    [styles.is__only_child]: iconIsOnlyChild,
                                                })}
                                            >
                                                {iconAfter}
                                            </span>
                                        )}
                                    </InnerWrapper>
                                </StyledButton>
                            )}
                        </Theme.Consumer>
                    )}
                </GlobalTheme.Consumer>
            </Theme.Provider>
        );
    }
}

const ButtonWithRef = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (props, ref) => <Button {...props} consumerRef={ref} />,
);
ButtonWithRef.displayName = 'Button';

export default ButtonWithRef;

